import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Flex,
  Heading,
  Select,
  Input,
  Text,
  useToast,
} from "@chakra-ui/react";
import {
  getTradingBalanceForecast,
  getCurrentBalance,
  getAccountList,
} from "../service/ForecastService";

interface TradingBalanceForecastingProps {
  onClose: () => void;
  onOpenAdvanced: () => void;
}

interface Account {
  accountId: string;
  accountName: string;
}

const TradingBalanceForecasting: React.FC<TradingBalanceForecastingProps> = ({
  onClose,
  onOpenAdvanced,
}) => {
  const [forecastDate, setForecastDate] = useState<string>("");
  const [accountId, setAccountId] = useState<string>("");
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [predictedBalance, setPredictedBalance] = useState<number | null>(null);
  const [currentBalance, setCurrentBalance] = useState<number | null>(null);
  const toast = useToast();

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const result = await getAccountList();
        if (result?.data) {
          setAccounts(result.data);
        }
      } catch (error) {
        toast({ title: "Failed to load account list", status: "error" });
      }
    };

    fetchAccounts();
  }, []);

  const fetchCurrentBalance = async () => {
    try {
      const result = await getCurrentBalance(accountId);
      setCurrentBalance(result.balance);
    } catch (error) {
      console.error("Failed to fetch current balance", error);
      toast({ title: "Failed to fetch current balance", status: "error" });
    }
  };

  const fetchPrediction = async () => {
    if (!forecastDate || !accountId) {
      toast({
        title: "Please select an Account and Forecast Date",
        status: "warning",
      });
      return;
    }

    try {
      const result = await getTradingBalanceForecast(accountId, forecastDate);
      setPredictedBalance(result.predictedBalance);
      await fetchCurrentBalance();
    } catch (error: any) {
      const serverMessage = error?.response?.data?.message;
      const fallbackMessage = "Error fetching prediction";
      toast({ title: serverMessage || fallbackMessage, status: "error" });
    }
  };

  return (
    <Container maxW="3xl" py={6}>
      <Heading size="lg" mb={4}>
        📊 Trading Balance Forecast
      </Heading>

      <Flex wrap="wrap" gap={4} mb={6} align="center">
        <Select
          placeholder="Select Account"
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
          width="250px"
        >
          {accounts.map((account) => (
            <option key={account.accountId} value={account.accountId}>
              {account.accountName}
            </option>
          ))}
        </Select>

        <Input
          type="datetime-local"
          value={forecastDate}
          onChange={(e) => setForecastDate(e.target.value)}
          width="250px"
        />

        <Button colorScheme="blue" onClick={fetchPrediction}>
          🔍 Predict Balance
        </Button>
      </Flex>

      <Divider mb={4} />

      {(predictedBalance !== null || currentBalance !== null) && (
        <Box p={6} bg="gray.100" borderRadius="lg" shadow="md">
          {currentBalance !== null && (
            <Box mb={4}>
              <Text fontSize="lg" fontWeight="bold">
                Current Balance:
              </Text>
              <Text fontSize="2xl" color="teal.600">
                ₹ {currentBalance.toFixed(2)}
              </Text>
            </Box>
          )}
          {predictedBalance !== null && (
            <Box>
              <Text fontSize="lg" fontWeight="bold">
                Predicted Balance at selected time:
              </Text>
              <Text fontSize="2xl" color="blue.600">
                ₹ {predictedBalance.toFixed(2)}
              </Text>
            </Box>
          )}
        </Box>
      )}

      <Box mt={6}>
        <Button variant="link" colorScheme="blue" onClick={onOpenAdvanced}>
          ← Back to Forecast Selection
        </Button>
      </Box>
    </Container>
  );
};

export default TradingBalanceForecasting;
